import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchGenerators,
  listJobs,
  listProjects,
  recentFiles,
  thumbnailUrl,
  type PrintJob,
  type Project,
  type RecentFile,
} from "../api";
import { HeroBand } from "../components/HeroBand";
import { useT } from "../i18n";
import { Icon } from "../components/Icon";
import { Empty, Spark } from "../components/ui";
import { PartThumb } from "../scene/PartThumb";

function Stat({ v, l, ico, spark }: { v: number | string; l: string; ico: string; spark?: number[] }) {
  return (
    <div className="card stat">
      <div className="ico"><Icon name={ico} /></div>
      <div className="v">{v}</div>
      <div className="l">{l}</div>
      {spark && (
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}>
          <Spark points={spark} />
        </div>
      )}
    </div>
  );
}

function PipelineStrip({ files, validated }: { files: number; validated: number }) {
  const tr = useT();
  const stages = [
    { tag: "STUDIO", label: "Conception & génération", val: `${files} fichiers`, color: "var(--accent)", icon: "studio", pct: 100 },
    { tag: "INSPECTOR", label: "Gate de printabilité", val: `${validated} ${tr("validés")} · ${files - validated} ${tr("à revoir")}`, color: "var(--ok)", icon: "shield", pct: files ? Math.round((validated / files) * 100) : 0 },
    { tag: "FOUNDRY", label: "Production & routage", val: "orient · slice · route", color: "var(--warn)", icon: "route", pct: 45 },
  ];
  return (
    <div className="card" style={{ marginBottom: 22, padding: 0, overflow: "hidden" }}>
      <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
        <div className="row" style={{ gap: 9 }}>
          <Icon name="layers" size={16} />
          <h2>Pipeline</h2>
        </div>
        <span className="eyebrow">{tr("idée → maillage validé → G-code routé")}</span>
      </div>
      <div className="pipeline-strip">
        {stages.map((s, i) => (
          <div key={s.tag} style={{ display: "contents" }}>
            <div style={{ padding: "18px 20px" }}>
              <div className="row" style={{ gap: 10, marginBottom: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 9, display: "grid", placeItems: "center", color: s.color, background: `color-mix(in oklab, ${s.color} 14%, transparent)`, border: `1px solid color-mix(in oklab, ${s.color} 38%, var(--line-2))` }}>
                  <Icon name={s.icon} size={16} />
                </div>
                <div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.1em", color: s.color }}>{s.tag}</div>
                  <div className="muted" style={{ fontSize: 12 }}>{tr(s.label)}</div>
                </div>
              </div>
              <div className="progress" style={{ marginBottom: 8 }}><span style={{ width: `${s.pct}%`, background: s.color }} /></div>
              <div className="num" style={{ fontSize: 12, color: "var(--text-2)" }}>{s.val}</div>
            </div>
            {i < stages.length - 1 && (
              <div className="pipeline-arrow" style={{ display: "grid", placeItems: "center", color: "var(--muted-2)" }}><Icon name="arrowRight" size={18} /></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const JOB_CFG: Record<string, [string, string, string]> = {
  printing: ["warn", "play", "Impression"],
  uploaded: ["accent", "wifi", "Envoyé"],
  queued: ["", "clock", "En file"],
  complete: ["ok", "check", "Terminé"],
  error: ["bad", "alert", "Erreur"],
};

export function DashboardPage() {
  const tr = useT();
  const name = localStorage.getItem("pf_name") || "";
  const [projects, setProjects] = useState<Project[]>([]);
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [recent, setRecent] = useState<RecentFile[]>([]);
  const [gens, setGens] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    listProjects().then(setProjects).catch(() => {});
    fetchGenerators().then((g) => setGens(g.length)).catch(() => {});
    listJobs().then(setJobs).catch(() => {});
    recentFiles(6).then(setRecent).catch(() => {});
  }, []);

  const files = projects.reduce((a, p) => a + (p.file_count || 0), 0);
  const validated = projects.reduce((a, p) => a + (p.ok_count || 0), 0);
  const printing = jobs.filter((j) => j.state === "printing").length;
  const live = jobs.filter((j) => ["printing", "uploaded", "queued"].includes(j.state));

  return (
    <div>
      <HeroBand
        image="/showcase/h2.jpg"
        eyebrow="Atelier"
        title={<>{tr("Bon retour")}{name ? `, ${name}` : ""} 👋</>}
        sub="Transforme une idée en pièce imprimable, validée pour ta machine — en une phrase."
        ctaLabel="Nouveau modèle"
        ctaTo="/create"
      >
        <Link to="/projects" className="btn"><Icon name="projects" size={15} /> {tr("Voir mes projets")}</Link>
      </HeroBand>

      <PipelineStrip files={files} validated={validated} />

      <div className="grid stats" style={{ marginBottom: 24 }}>
        <Stat v={projects.length} l={tr("Projets actifs")} ico="projects" spark={[3, 4, 4, 5, 5, 6, projects.length || 6]} />
        <Stat v={files} l={tr("Fichiers 3D")} ico="box" spark={[6, 7, 9, 9, 11, 12, files || 12]} />
        <Stat v={gens} l={tr("Générateurs")} ico="generators" />
        <Stat v={printing} l={tr("Impression en cours")} ico="printer" spark={[0, 1, 1, 0, 1, 2, printing]} />
      </div>

      {/* Latest generations -- visual preview strip */}
      {recent.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div className="row between" style={{ marginBottom: 14 }}>
            <h2>{tr("Dernières générations")}</h2>
            <Link className="btn ghost sm" to="/projects">{tr("Tout voir")} <Icon name="arrowRight" size={13} /></Link>
          </div>
          <div className="grid cards" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
            {recent.map((f) => (
              <div key={f.id} className="card hover" style={{ padding: 12, cursor: "pointer" }} onClick={() => nav(`/studio?project=${f.project_id}&file=${f.id}`)} title={tr("Ouvrir dans le Studio 3D")}>
                <PartThumb material={f.material} passed={f.passed} size={156} src={f.has_thumb ? thumbnailUrl(f.project_id, f.id) : undefined} />
                <div className="card-title" style={{ fontSize: 13, marginTop: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</div>
                <div className="row between" style={{ marginTop: 4 }}>
                  <span className="muted" style={{ fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 110 }}>{f.project_name}</span>
                  <span className="num" style={{ fontSize: 11.5, color: f.score >= 0.85 ? "var(--ok)" : f.score >= 0.7 ? "var(--warn)" : "var(--bad)" }}>{f.score.toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="dash-cols" style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 22, alignItems: "start" }}>
        <div>
          <div className="row between" style={{ marginBottom: 14 }}>
            <h2>{tr("Projets récents")}</h2>
            <Link className="btn ghost sm" to="/projects">{tr("Tout voir")} <Icon name="arrowRight" size={13} /></Link>
          </div>
          {projects.length === 0 ? (
            <Empty icon="projects">{tr("Aucun projet.")} <Link to="/projects" style={{ color: "var(--accent-3)" }}>{tr("Créez-en un")}</Link>.</Empty>
          ) : (
            <div className="grid cards" style={{ gridTemplateColumns: "1fr 1fr" }}>
              {projects.slice(0, 4).map((p) => (
                <div key={p.id} className="card hover" onClick={() => nav(`/projects/${p.id}`)}>
                  <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
                    <PartThumb material={p.preview_material} passed={p.preview_passed} size={64} empty={(p.file_count ?? 0) === 0} src={p.preview_file_id ? thumbnailUrl(p.id, p.preview_file_id) : undefined} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="card-row" style={{ marginBottom: 6 }}>
                        <span className="card-title" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                        {p.shared && <span className="badge accent">{tr("partagé")}</span>}
                      </div>
                      <div className="row between">
                        <span className="num muted" style={{ fontSize: 11.5 }}>{p.file_count ?? 0} fichier(s)</span>
                        {(p.file_count ?? 0) > 0 && (
                          <span className="badge ok"><span className="dot" /><span className="num">{p.ok_count ?? 0}/{p.file_count}</span></span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="row between" style={{ marginBottom: 14 }}>
            <h2>Production en direct</h2>
            <Link className="btn ghost sm" to="/production">Monitoring <Icon name="arrowRight" size={13} /></Link>
          </div>
          {live.length === 0 && jobs.length === 0 ? (
            <Empty icon="route">{tr("Aucun job. Routez un modèle validé vers une imprimante.")}</Empty>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              {(live.length ? live : jobs).slice(0, 6).map((j, i, arr) => {
                const cfg = JOB_CFG[j.state] ?? ["", "clock", j.state];
                return (
                  <div key={j.id} style={{ padding: "13px 16px", borderBottom: i < arr.length - 1 ? "1px solid var(--line)" : "none" }}>
                    <div className="row between" style={{ marginBottom: 7 }}>
                      <span className="row" style={{ gap: 8, fontWeight: 500, fontSize: 13, minWidth: 0 }}>
                        <PartThumb material={j.material} size={26} />
                        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{j.filename}</span>
                      </span>
                      <span className={`badge ${cfg[0]}`}><Icon name={cfg[1]} size={11} />{tr(cfg[2])}</span>
                    </div>
                    <div className="row between">
                      <span className="muted" style={{ fontSize: 11.5 }}>{j.printer_name}</span>
                      {j.eta && j.eta !== "--" && <span className="num muted" style={{ fontSize: 11.5 }}>{j.eta}</span>}
                    </div>
                    {j.state === "printing" && j.progress != null && (
                      <div className="progress" style={{ marginTop: 8, height: 5 }}><span style={{ width: `${Math.round((j.progress || 0) * 100)}%`, background: "var(--warn)" }} /></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
